import type {QueryClient} from "@tanstack/react-query";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useCallback} from "react";
import {invalidateOttopilotDataViewerQueries} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {OttopilotDispositionPluginKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import type {OttopilotTriggerConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerTriggerConfig";
import type {OttopilotDispositionConfig} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainerDispositionConfig";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {createEvent, type EventWithEventContainer} from "~/utilities/apis/api";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import {buildInitialEditableEventFromTrigger, buildEventContainerAdditionalColumnValues, resolveActiveTrigger} from "~/utilities/eventContainerTriggerEvent";
import {
    buildAmaronJourneyDisposeArgsFromListEvent as buildAmaronEventContainerDisposeArgsFromListEvent,
    submitAmaronJourneyDispose as submitAmaronEventContainerDispose,
} from "~/utilities/workspaceSpecific/amaronJourneyDispose";
import {submitDuluxJourneyDispose as submitDuluxEventContainerDispose} from "~/utilities/workspaceSpecific/duluxJourneyDispose";
import {
    getEventContainerDispositionColumnId,
    getEventContainerDispositionId,
    requireEventContainerTemplate,
} from "~/utilities/ottopilotSemanticColumns";

export function useEventContainerListDispositionSave({
    event,
    triggerConfiguration,
    dispositionConfiguration,
    remarksColumnId,
    queryKeyPrefix,
    getLeadDisplayName,
    invalidateQueriesBeforeDataViewer,
    onDispositionSaved,
    refetchDataViewerAfterSave = true,
    invalidateDataViewerQueries,
    onRefreshStateChange,
    onClose,
}: {
    event: EventWithEventContainer;
    triggerConfiguration: OttopilotTriggerConfig | undefined;
    dispositionConfiguration: OttopilotDispositionConfig;
    remarksColumnId: Uuid | null;
    queryKeyPrefix: ReadonlyArray<string | null>;
    getLeadDisplayName: (event: EventWithEventContainer) => string;
    invalidateQueriesBeforeDataViewer?: () => Promise<void>;
    onDispositionSaved?: (args: {eventContainerId: Uuid; dispositionId: Uuid}) => void | Promise<void>;
    refetchDataViewerAfterSave?: boolean;
    invalidateDataViewerQueries?: (queryClient: QueryClient, queryKeyPrefix: ReadonlyArray<string | null>) => Promise<void>;
    onRefreshStateChange?: (isRefreshing: boolean) => void;
    onClose: () => void;
}) {
    const {eventContainerTemplates, eventTemplates, workspace, workspaceCapabilities} = useWorkspace();
    const {heimdallUser, workspaceUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const queryClient = useQueryClient();
    const {showFlag} = useAnankeFlags();
    const eventContainerTemplate = requireEventContainerTemplate(eventContainerTemplates, event.eventContainer.templateId);
    const dispositionColumnId = getEventContainerDispositionColumnId(eventContainerTemplate);
    const eventContainerDispositionId = getEventContainerDispositionId(event.eventContainer, eventContainerTemplate);

    const invalidateDataViewer = useCallback(async () => {
        if (invalidateDataViewerQueries !== undefined) {
            await invalidateDataViewerQueries(queryClient, queryKeyPrefix);
            return;
        }
        await invalidateOttopilotDataViewerQueries(queryClient, queryKeyPrefix);
    }, [invalidateDataViewerQueries, queryClient, queryKeyPrefix]);

    const saveMutation = useMutation({
        mutationKey: ["EventContainerListDispositionPanel", "save", event.eventContainer.id],
        onMutate: () => {
            onRefreshStateChange?.(true);
        },
        mutationFn: async (args: {
            dispositionId: Uuid;
            suggestedStatus: Uuid | null;
            remarks: string | null;
            trigger: {additionalColumnValues: Record<Uuid, unknown>} | null;
        }) => {
            if (isAmaronWorkspace(workspaceCapabilities) === true) {
                const disposeResult = await submitAmaronEventContainerDispose(
                    buildAmaronEventContainerDisposeArgsFromListEvent({
                        accessToken: heimdallUser.accessToken,
                        intellsysBaseUrl: intellsysBaseUrl,
                        workspaceId: workspace.id,
                        event: event,
                        dispositionId: args.dispositionId,
                        remarks: args.remarks,
                        remarksColumnId: remarksColumnId ?? undefined,
                        triggerEventAdditionalColumnValues: args.trigger?.additionalColumnValues,
                    }),
                );
                if (disposeResult.success !== true) {
                    throw disposeResult.error;
                }

                if (invalidateQueriesBeforeDataViewer !== undefined) {
                    await invalidateQueriesBeforeDataViewer();
                }
                if (onDispositionSaved !== undefined) {
                    await onDispositionSaved({
                        eventContainerId: event.eventContainer.id,
                        dispositionId: args.dispositionId,
                    });
                }
                if (refetchDataViewerAfterSave === true) {
                    await invalidateDataViewer();
                }
                await queryClient.invalidateQueries({
                    queryKey: ["getLastNDispositionIdsForEventContainer", event.eventContainer.id, workspace.id],
                });
                return;
            }

            if (isDuluxWorkspace(workspaceCapabilities) === true) {
                const disposeResult = await submitDuluxEventContainerDispose({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl: intellsysBaseUrl,
                    workspaceId: workspace.id,
                    event: event,
                    dispositionId: args.dispositionId,
                    remarks: args.remarks,
                    triggerEventAdditionalColumnValues: args.trigger?.additionalColumnValues,
                });
                if (disposeResult.success !== true) {
                    throw disposeResult.error;
                }

                if (invalidateQueriesBeforeDataViewer !== undefined) {
                    await invalidateQueriesBeforeDataViewer();
                }
                if (onDispositionSaved !== undefined) {
                    await onDispositionSaved({
                        eventContainerId: event.eventContainer.id,
                        dispositionId: args.dispositionId,
                    });
                }
                if (refetchDataViewerAfterSave === true) {
                    await invalidateDataViewer();
                }
                await queryClient.invalidateQueries({
                    queryKey: ["getLastNDispositionIdsForEventContainer", event.eventContainer.id, workspace.id],
                });
                return;
            }

            const additionalColumns: Record<Uuid, unknown> = {};

            const dispositionStatusColumnId = dispositionConfiguration.plugins.find(
                (plugin) => plugin.kind === OttopilotDispositionPluginKind.suggestedJourneyStatusRules,
            )?.dispositionStatusColumnId;
            if (dispositionStatusColumnId !== undefined && args.suggestedStatus !== null) {
                additionalColumns[dispositionStatusColumnId] = args.suggestedStatus;
            }

            if (remarksColumnId !== null && args.remarks !== null) {
                additionalColumns[remarksColumnId] = args.remarks;
            }

            const activeTrigger = resolveActiveTrigger(triggerConfiguration, args.dispositionId);
            if (activeTrigger !== undefined) {
                const triggerRuntimeContext = {
                    eventContainerId: event.eventContainer.id,
                    eventContainerDispositionId: eventContainerDispositionId,
                    heimdallUserId: heimdallUser.id,
                    workspaceId: workspace.id,
                    workspaceUser: {
                        userId: workspaceUser.userId,
                        templateId: workspaceUser.templateId,
                    },
                    selectedDispositionId: args.dispositionId,
                    eventContainerAdditionalColumnValues: event.eventContainer.additionalColumnValues,
                    getEventColumnValue: (referenceField: Uuid) => {
                        const fromRow = event.additionalColumnValues[referenceField];
                        if (fromRow === undefined || fromRow === null) {
                            return undefined;
                        }
                        return fromRow;
                    },
                };
                const triggerEventTemplate = eventTemplates.find((template) => template.id === activeTrigger.eventTemplateId);
                if (triggerEventTemplate === undefined) {
                    throw new Error(`Trigger Event template not found: ${activeTrigger.eventTemplateId}`);
                }
                const initialTriggerEvent = buildInitialEditableEventFromTrigger(activeTrigger, triggerEventTemplate, triggerRuntimeContext);
                if (args.trigger !== null) {
                    Object.assign(initialTriggerEvent.additionalColumnValues, args.trigger.additionalColumnValues);
                }
                const createTriggerResult = await createEvent({
                    accessToken: heimdallUser.accessToken,
                    intellsysBaseUrl,
                    event: initialTriggerEvent,
                    workspaceId: workspace.id,
                });
                if (createTriggerResult.success !== true) {
                    throw createTriggerResult.error;
                }
                const eventContainerAdditionalColumnValues = buildEventContainerAdditionalColumnValues(activeTrigger, triggerRuntimeContext);
                Object.assign(additionalColumns, eventContainerAdditionalColumnValues);
            }

            const updateEventContainerResult = await updateEventContainer({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl,
                id: event.eventContainer.id,
                data: {
                    ...additionalColumns,
                    [dispositionColumnId]: args.dispositionId,
                },
                templateId: event.eventContainer.templateId,
                workspaceId: workspace.id,
            });
            if (updateEventContainerResult.success !== true) {
                throw updateEventContainerResult.error;
            }

            if (invalidateQueriesBeforeDataViewer !== undefined) {
                await invalidateQueriesBeforeDataViewer();
            }
            if (onDispositionSaved !== undefined) {
                await onDispositionSaved({
                    eventContainerId: event.eventContainer.id,
                    dispositionId: args.dispositionId,
                });
            }
            if (refetchDataViewerAfterSave === true) {
                await invalidateDataViewer();
            }
            await queryClient.invalidateQueries({
                queryKey: ["getLastNDispositionIdsForEventContainer", event.eventContainer.id, workspace.id],
            });
        },
        onSuccess: () => {
            showFlag({
                title: "Disposition has been updated against",
                description: getLeadDisplayName(event),
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 5,
            });
        },
        onSettled: (_data, error) => {
            if (error === null || error === undefined) {
                onClose();
                return;
            }
            onRefreshStateChange?.(false);
        },
        onError: (err: unknown) => {
            const message = err instanceof Error ? err.message : String(err);
            showFlag({
                title: "Failed to update disposition",
                description: message,
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 5,
            });
        },
    });

    const handleSave = useCallback(
        (args: {
            dispositionId: Uuid;
            suggestedStatus: Uuid | null;
            remarks: string | null;
            trigger: {additionalColumnValues: Record<Uuid, unknown>} | null;
        }) => {
            saveMutation.mutate(args);
        },
        [saveMutation],
    );

    return {
        saveMutation,
        handleSave,
        isSaving: saveMutation.isPending === true,
    };
}
